/**
 * Templates — list, create, duplicate, delete, preview with a real contact,
 * export to / import from a JSON file.
 *
 * The file is the one the standalone zaprinta-mailings app writes, so templates
 * travel both ways between the two (and between accounts or instances here).
 * An import always creates a new template, never overwrites one.
 *
 * The list deliberately fetches metadata only; a rendered template runs to
 * hundreds of KB and the editor loads the body on demand.
 */
define('mailingTemplatesView',[
  'module',
  'app',
  'underscore',
  'jquery',
  'marionette',
  'mailingShell',
  'entities/mailing'
], function (
  module,
  App,
  _,
  $,
  Marionette,
  Shell,
  MailingApi
) {
  'use strict';

  var TemplatesView = Marionette.ItemView.extend({

    className: 'mailing-page mailing-templates',

    template: _.template(
      '<%= nav %>' +
      '<div class="mlg-body"><div class="mlg-loading">Loading templates…</div></div>' +
      '<div class="mlg-modal" style="display:none"></div>'
    ),

    ui: {
      body: '.mlg-body',
      modal: '.mlg-modal'
    },

    events: {
      'click .mlg-new-template': 'onNew',
      'click .mlg-open': 'onOpen',
      'click .mlg-duplicate': 'onDuplicate',
      'click .mlg-export': 'onExport',
      'click .mlg-import-template': 'onImportClick',
      'change .f-import-file': 'onImportFiles',
      'click .mlg-preview': 'onPreview',
      'input .f-preview-search': 'onPreviewSearch',
      'click .mlg-preview-pick': 'onPreviewPick',
      'click .mlg-preview-clear': 'onPreviewClear',
      'click .mlg-delete': 'onDelete',
      'click .mlg-modal-cancel': 'closeModal',
      'click .mlg-modal-save': 'onCreate'
    },

    serializeData: function () {
      return {
        nav: Shell.navHtml('templates',
          '<input type="file" class="f-import-file" accept=".json,application/json" multiple style="display:none">' +
          '<button type="button" class="mlg-btn mlg-import-template" ' +
            'title="Import templates exported from here or from zaprinta-mailings (.json)">Import</button>' +
          '<button type="button" class="mlg-btn primary mlg-new-template">New template</button>')
      };
    },

    onShow: function () {
      this.load();
    },

    load: function () {
      var self = this;
      MailingApi.templates().done(function (templates) {
        self.renderList(templates || []);
      }).fail(function (xhr) {
        self.ui.body.html('<div class="mlg-error">' + _.escape(MailingApi.errorMessage(xhr)) + '</div>');
      });
    },

    renderList: function (templates) {
      if (!templates.length) {
        this.ui.body.html(Shell.emptyHtml('No template yet. Create one to start designing an email.'));
        return;
      }
      var rows = _.map(templates, function (template) {
        return '<tr data-secid="' + template.secId + '">' +
          '<td class="c-name">' +
          '  <a class="mlg-open" href="#mailing/templates/' + template.secId + '">' +
               _.escape(template.name || '(untitled)') + '</a>' +
          '  <div class="c-sub">' + _.escape(template.subject || '') + '</div>' +
          '</td>' +
          '<td>' + _.escape(template.language || 'EN') + '</td>' +
          '<td>' + Shell.formatDate(template.modDate, true) + '</td>' +
          '<td class="c-actions">' +
          '  <button type="button" class="mlg-btn small mlg-preview">Preview</button>' +
          '  <button type="button" class="mlg-btn small mlg-duplicate">Duplicate</button>' +
          '  <button type="button" class="mlg-btn small mlg-export" title="Download as a .json file">Export</button>' +
          '  <button type="button" class="mlg-btn small danger mlg-delete">Delete</button>' +
          '</td></tr>';
      }).join('');

      this.ui.body.html(
        '<table class="mlg-table">' +
        '<thead><tr><th>Template</th><th>Language</th><th>Last modified</th><th></th></tr></thead>' +
        '<tbody>' + rows + '</tbody></table>');
    },

    onNew: function () {
      this.ui.modal.html(
        '<div class="mlg-modal-card narrow">' +
        '<h3>New template</h3>' +
        '<label>Name<input type="text" class="f-name"></label>' +
        '<label>Subject<input type="text" class="f-subject"></label>' +
        '<label>Language<input type="text" class="f-language" value="EN"></label>' +
        '<div class="mlg-modal-actions">' +
        '  <button type="button" class="mlg-btn mlg-modal-cancel">Cancel</button>' +
        '  <button type="button" class="mlg-btn primary mlg-modal-save">Create and design</button>' +
        '</div>' +
        '</div>').show();
    },

    onCreate: function () {
      var self = this;
      var name = this.ui.modal.find('.f-name').val();
      if (!name) {
        Shell.banner(this.$el, 'Name is required', 'error');
        return;
      }
      MailingApi.createTemplate({
        name: name,
        subject: this.ui.modal.find('.f-subject').val(),
        language: this.ui.modal.find('.f-language').val() || 'EN',
        htmlContent: '',
        designJson: ''
      }).done(function (template) {
        self.closeModal();
        App.navigate('mailing/templates/' + template.secId, { trigger: true });
      }).fail(function (xhr) {
        Shell.banner(self.$el, MailingApi.errorMessage(xhr), 'error');
      });
    },

    /** Copied server-side, body included, so a duplicate opens ready to tweak. */
    onDuplicate: function (e) {
      var self = this;
      MailingApi.duplicateTemplate(this.secIdOf(e)).done(function () {
        Shell.banner(self.$el, 'Template duplicated', 'ok');
        self.load();
      }).fail(function (xhr) {
        Shell.banner(self.$el, MailingApi.errorMessage(xhr), 'error');
      });
    },

    //----------Export / import----------------------------------------------//

    /** Same naming as the standalone app: every non-alphanumeric character becomes "_". */
    exportFileName: function (name) {
      var base = String(name || '').replace(/[^A-Za-z0-9]/g, '_');
      return (base.replace(/_/g, '') ? base.substring(0, 120) : 'template') + '.json';
    },

    /**
     * Fetched through the API client and saved from a Blob rather than opened as
     * a link: the download then needs no session token in a URL, and the file is
     * pretty-printed — it is meant to be readable and diffable.
     */
    onExport: function (e) {
      var self = this;
      MailingApi.exportTemplate(this.secIdOf(e)).done(function (file) {
        var blob = new window.Blob([JSON.stringify(file, null, 2)], { type: 'application/json' });
        var link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = self.exportFileName(file.name);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.setTimeout(function () { window.URL.revokeObjectURL(link.href); }, 1000);
      }).fail(function (xhr) {
        Shell.banner(self.$el, MailingApi.errorMessage(xhr), 'error');
      });
    },

    onImportClick: function () {
      this.$('.f-import-file').click();
    },

    onImportFiles: function (e) {
      var files = _.toArray(e.currentTarget.files);
      e.currentTarget.value = '';   // so choosing the same file again still fires "change"
      if (files.length) {
        this.importFiles(files);
      }
    },

    /**
     * One file after the other, so that a bad one is reported by name and the
     * rest still come in. A single import opens straight in the editor; several
     * stay on the list.
     */
    importFiles: function (files) {
      var self = this;
      var total = files.length;
      var imported = [];
      var problems = [];

      function finish() {
        var notes = _.flatten(_.map(imported, function (result) {
          return _.map(result.warnings || [], function (warning) {
            return '"' + result.template.name + '": ' + warning;
          });
        }));
        if (total === 1 && imported.length === 1 && !notes.length) {
          App.navigate('mailing/templates/' + imported[0].template.secId, { trigger: true });
          return;
        }
        self.load();
        var summary = imported.length + ' of ' + total + ' template' + (total === 1 ? '' : 's') + ' imported';
        if (problems.length || notes.length) {
          self.showImportReport(summary, problems, notes);
        } else {
          Shell.banner(self.$el, summary, 'ok');
        }
      }

      function next() {
        var file = files.shift();
        if (!file) {
          finish();
          return;
        }
        var reader = new window.FileReader();
        reader.onerror = function () {
          problems.push('"' + file.name + '": the file could not be read');
          next();
        };
        reader.onload = function () {
          var parsed;
          try {
            parsed = JSON.parse(reader.result);
          } catch (error) {
            problems.push('"' + file.name + '": not a JSON file');
            next();
            return;
          }
          MailingApi.importTemplate(parsed).done(function (result) {
            imported.push(result);
          }).fail(function (xhr) {
            problems.push('"' + file.name + '": ' + MailingApi.errorMessage(xhr));
          }).always(next);
        };
        reader.readAsText(file);
      }
      next();
    },

    /** Warnings can be long (they list image URLs), so they get a dialog, not the 8-second banner. */
    showImportReport: function (summary, problems, notes) {
      var list = function (title, lines, cssClass) {
        return lines.length ? '<h4>' + title + '</h4><ul class="mlg-report ' + cssClass + '">' +
          _.map(lines, function (line) { return '<li>' + _.escape(line) + '</li>'; }).join('') + '</ul>' : '';
      };
      this.ui.modal.html(
        '<div class="mlg-modal-card">' +
        '<h3>' + _.escape(summary) + '</h3>' +
        list('Not imported', problems, 'error') +
        list('Imported, but check', notes, 'warn') +
        '<div class="mlg-modal-actions">' +
        '  <button type="button" class="mlg-btn primary mlg-modal-cancel">OK</button>' +
        '</div>' +
        '</div>').show();
    },

    //----------Preview with a contact---------------------------------------//
    // Opens with the merge tags still visible; picking a contact fills them in
    // with that contact's data — the quickest way to spot a field that renders
    // badly for real people (empty first names, long webshop URLs…).

    onPreview: function (e) {
      var name = $(e.currentTarget).closest('tr').find('.c-name a').text();
      this.previewSecId = this.secIdOf(e);
      this.ui.modal.html(
        '<div class="mlg-modal-card wide mlg-preview-card">' +
        '<h3>Preview — ' + _.escape(name) + '</h3>' +
        '<div class="mlg-preview-search">' +
        '  <input type="text" class="f-preview-search" placeholder="Search a contact by name or email…">' +
        '  <div class="mlg-preview-results" style="display:none"></div>' +
        '</div>' +
        '<div class="mlg-preview-contact" style="display:none"></div>' +
        '<div class="mlg-preview-host"><div class="mlg-loading">Loading preview…</div></div>' +
        '<div class="mlg-modal-actions">' +
        '  <button type="button" class="mlg-btn mlg-modal-cancel">Close</button>' +
        '</div>' +
        '</div>').show();
      this.loadPreview(null);
    },

    loadPreview: function (subscriberSecId) {
      var $host = this.ui.modal.find('.mlg-preview-host');
      MailingApi.previewTemplate(this.previewSecId, subscriberSecId).done(function (result) {
        Shell.showEmailHtml($host, (result || {}).html);
      }).fail(function (xhr) {
        $host.html('<div class="mlg-error">' + _.escape(MailingApi.errorMessage(xhr)) + '</div>');
      });
    },

    onPreviewSearch: function (e) {
      var query = $.trim($(e.currentTarget).val());
      var $results = this.ui.modal.find('.mlg-preview-results');
      window.clearTimeout(this.previewSearchTimer);
      if (!query) {
        $results.hide().empty();
        return;
      }
      this.previewSearchTimer = window.setTimeout(function () {
        MailingApi.subscribers({ search: query, limit: 20 }).done(function (page) {
          var rows = _.map((page || {}).data || [], function (subscriber) {
            return '<button type="button" class="mlg-preview-pick" data-secid="' + subscriber.secId + '">' +
              '<strong>' + _.escape($.trim((subscriber.firstName || '') + ' ' + (subscriber.lastName || ''))) +
              '</strong> <span>' + _.escape(subscriber.email || '') + '</span></button>';
          }).join('');
          $results.html(rows || '<div class="mlg-hint">No contact matches.</div>').show();
        });
      }, 250);
    },

    onPreviewPick: function (e) {
      var $picked = $(e.currentTarget);
      this.ui.modal.find('.mlg-preview-results').hide().empty();
      this.ui.modal.find('.f-preview-search').val('');
      this.ui.modal.find('.mlg-preview-contact')
        .html('Showing the email as received by <strong>' + _.escape($picked.find('span').text()) +
          '</strong> <button type="button" class="mlg-btn small mlg-preview-clear">Clear</button>')
        .show();
      this.loadPreview($picked.data('secid'));
    },

    onPreviewClear: function () {
      this.ui.modal.find('.mlg-preview-contact').hide().empty();
      this.loadPreview(null);
    },

    onDelete: function (e) {
      var self = this;
      var name = $(e.currentTarget).closest('tr').find('.c-name a').text();
      if (!window.confirm('Delete "' + name + '"? Campaigns using it will need a new template.')) { return; }

      MailingApi.deleteTemplate(this.secIdOf(e)).done(function () {
        Shell.banner(self.$el, 'Template deleted', 'ok');
        self.load();
      }).fail(function (xhr) {
        Shell.banner(self.$el, MailingApi.errorMessage(xhr), 'error');
      });
    },

    onOpen: function (e) {
      e.preventDefault();
      App.navigate($(e.currentTarget).attr('href').substring(1), { trigger: true });
    },

    closeModal: function () {
      window.clearTimeout(this.previewSearchTimer);
      this.ui.modal.hide().empty();
    },

    onDestroy: function () {
      window.clearTimeout(this.previewSearchTimer);
    },

    secIdOf: function (e) {
      return $(e.currentTarget).closest('tr').data('secid');
    }
  });

  module.exports = TemplatesView;
});

